import { Badge, Button, Card } from "@design";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import type { Proposal } from "./useInbox";

function Entity({ entity }: Readonly<{ entity: Proposal["subject"] }>) {
  const { t } = useTranslation("knowledge");
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Text as="span" variant="body">
        {entity.name}
      </Text>
      {!entity.node && <Badge variant="info">{t("inbox.newPage")}</Badge>}
      {entity.matchedAs && (
        <Text as="span" variant="caption" tone="tertiary">
          {t("inbox.matchedAs", { mention: entity.matchedAs.mention, reason: t(`reason.${entity.matchedAs.reason}`) })}
        </Text>
      )}
    </span>
  );
}

/** One proposed fact: what it says, where it came from, and the two decisions. */
export function ProposalCard({
  proposal,
  onAccept,
  onReject,
  rejecting,
}: Readonly<{ proposal: Proposal; onAccept: () => void; onReject: () => void; rejecting: boolean }>) {
  const { t } = useTranslation("knowledge");
  return (
    <Card>
      <article aria-label={proposal.summary} className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Entity entity={proposal.subject} />
            <Text as="span" tone="secondary">
              {t(`relation.${proposal.relation}`)}
            </Text>
            <Entity entity={proposal.object} />
            {proposal.role && (
              <Text as="span" tone="secondary">
                {t("inbox.role", { role: proposal.role })}
              </Text>
            )}
          </p>
          <Badge variant="neutral">{t("inbox.confidence", { percent: Math.round(proposal.confidence * 100) })}</Badge>
        </div>
        {proposal.evidence && (
          <figure className="flex flex-col gap-1 border-l-2 border-border-default pl-3">
            <Text as="figcaption" variant="caption" tone="tertiary">
              {proposal.importFilename
                ? t("inbox.evidenceFrom", { source: proposal.importFilename })
                : t("inbox.evidence")}
            </Text>
            <Text as="blockquote" variant="body-sm" tone="secondary">
              {proposal.evidence}
            </Text>
          </figure>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" size="sm" loading={rejecting} onClick={onReject}>
            {t("inbox.reject")}
          </Button>
          <Button variant="primary" size="sm" onClick={onAccept}>
            {t("inbox.accept")}
          </Button>
        </div>
      </article>
    </Card>
  );
}
